@if (defaultLanguage(); as defaultLang) {
  @let isDefaultEditable = defaultEditable() && !isReadonly(defaultLang.code);
  <div class="translation-language-list__default">
    <div class="translation-language-list__group-header" aria-hidden="true">
      <span class="translation-language-list__group-label">{{
        'SHARED.TRANSLATE_DIALOG.DEFAULT_GROUP_LABEL' | translate
      }}</span>
    </div>
    <div
      class="translation-language-list__row translation-language-list__row--default"
      [class.translation-language-list__row--default-editable]="isDefaultEditable">
      <div class="translation-language-list__row-lang">
        <span
          class="flag-icon flag-icon-round"
          [class]="'flag-icon-' + defaultLang.code"
          aria-hidden="true"></span>
        <span class="translation-language-list__row-name">{{ displayName(defaultLang) }}</span>
      </div>
      @if (isDefaultEditable) {
        <app-input-with-counter
          #defaultInput
          class="translation-language-list__default-input"
          [maxLength]="maxLength()"
          [placeholder]="defaultPlaceholder()"
          [required]="true"
          [autoFocus]="true"
          [ngModel]="defaultValue()"
          (ngModelChange)="defaultValue.set($event)"
          [attr.aria-label]="fieldLabel() + ' in ' + displayName(defaultLang)"
          [attr.data-testid]="testIdFor(defaultLang.code)"
          ngDefaultControl />
        @if (defaultValue().trim()) {
          <span class="translation-language-list__status-ring" aria-hidden="true">
            <mat-icon>check</mat-icon>
          </span>
        } @else {
          <span
            class="translation-language-list__status-required"
            aria-hidden="true"
            [attr.title]="'SHARED.TRANSLATE_DIALOG.STATUS_REQUIRED' | translate"
            >*</span
          >
        }
      } @else {
        <span class="translation-language-list__default-value">{{ defaultValue() }}</span>
      }
    </div>
  </div>
}

<ng-template #suggestAllButton>
  <span
    class="translation-language-list__suggest-all"
    [appAiUpsellPopover]="!aiAvailable()"
    [attr.tabindex]="aiAvailable() ? null : 0"
    [attr.aria-label]="
      aiAvailable() ? null : ('SHARED.TRANSLATE_DIALOG.AI_REQUIRES_INTELLIGENCE' | translate)
    ">
    <button
      type="button"
      class="translation-language-list__ai-btn"
      [class.translation-language-list__ai-btn--loading]="isSuggesting()"
      [disabled]="suggestDisabled()"
      (click)="suggestAll.emit()"
      [attr.aria-label]="'SHARED.TRANSLATE_DIALOG.AI_SUGGEST_ALL' | translate">
      @if (isSuggesting()) {
        <span class="translation-language-list__ai-spinner" aria-hidden="true"></span>
      } @else {
        <span class="translation-language-list__ai-glyph" aria-hidden="true"></span>
      }
      {{
        isSuggesting()
          ? ('SHARED.TRANSLATE_DIALOG.AI_SUGGESTING' | translate)
          : ('SHARED.TRANSLATE_DIALOG.AI_SUGGEST_ALL' | translate)
      }}
    </button>
  </span>
</ng-template>

<div class="translation-language-list__list">
  <div class="translation-language-list__group-header">
    <span class="translation-language-list__group-label" aria-hidden="true">{{
      'SHARED.TRANSLATE_DIALOG.ACTIVE_LANGUAGES_LABEL' | translate
    }}</span>
    <span class="translation-language-list__group-count" aria-hidden="true">{{
      languages().length
    }}</span>
    <ng-container [ngTemplateOutlet]="suggestAllButton" />
  </div>

  <ul class="translation-language-list__rows">
    @for (lang of languages(); track lang.code) {
      <li class="translation-language-list__row">
        <ng-container
          [ngTemplateOutlet]="languageRow"
          [ngTemplateOutletContext]="{ $implicit: lang }" />
      </li>
    }
  </ul>

  @if (inactiveLanguages().length > 0) {
    <button
      type="button"
      class="translation-language-list__group-header translation-language-list__group-header--toggle"
      (click)="inactiveExpanded.set(!inactiveExpanded())"
      [attr.aria-expanded]="inactiveExpanded()"
      data-testid="btn-toggle-inactive-languages">
      <span class="translation-language-list__group-label">{{
        'SHARED.TRANSLATE_DIALOG.INACTIVE_GROUP_LABEL' | translate
      }}</span>
      <span class="translation-language-list__group-count">{{ inactiveLanguages().length }}</span>
      <mat-icon
        class="translation-language-list__chevron"
        [class.translation-language-list__chevron--open]="inactiveExpanded()"
        aria-hidden="true"
        >expand_more</mat-icon
      >
    </button>
    @if (inactiveExpanded()) {
      <div
        class="translation-language-list__collapse"
        animate.enter="translation-language-list__collapse--enter"
        animate.leave="translation-language-list__collapse--leave">
        <ul class="translation-language-list__rows translation-language-list__rows--inactive">
          @for (lang of inactiveLanguages(); track lang.code) {
            <li class="translation-language-list__row">
              <ng-container
                [ngTemplateOutlet]="languageRow"
                [ngTemplateOutletContext]="{ $implicit: lang }" />
            </li>
          }
        </ul>
      </div>
    }
  }
</div>

<ng-template #languageRow let-lang>
  <div class="translation-language-list__row-lang">
    <span
      class="flag-icon flag-icon-round"
      [class]="'flag-icon-' + lang.code"
      aria-hidden="true"></span>
    <span class="translation-language-list__row-name">{{ displayName(lang) }}</span>
  </div>
  <mat-form-field
    class="translation-language-list__input"
    subscriptSizing="dynamic"
    [class.translation-language-list__input--with-stem]="stemFor(lang.code)"
    [class.translation-language-list__input--translating]="isSuggesting()">
    @if (stemFor(lang.code); as stem) {
      <mat-label>{{ stem }}</mat-label>
    }
    <input
      matInput
      type="text"
      [ngModel]="values()[lang.code] ?? ''"
      (ngModelChange)="setValue(lang.code, $event)"
      [attr.maxlength]="maxLength()"
      [disabled]="isReadonly(lang.code) || isSuggesting()"
      [placeholder]="
        'SHARED.TRANSLATE_DIALOG.PLACEHOLDER_TRANSLATE_TO'
          | translate: { language: displayName(lang) }
      "
      [attr.aria-label]="fieldLabel() + ' in ' + displayName(lang)"
      [aria-describedby]="failureFor(lang.code) ? noteIdFor(lang.code) : ''"
      [attr.data-testid]="testIdFor(lang.code)" />
  </mat-form-field>
  @if (isAiTranslated(lang.code)) {
    <app-ai-disclosure
      class="translation-language-list__ai-badge"
      kind="translation"
      variant="chip"
      [showIcon]="false"
      [showLearnMore]="false"
      [testId]="'ai-disclosure-translation-' + lang.code" />
  }
  @if (completionMap()[lang.code]) {
    <span class="translation-language-list__status-ring" aria-hidden="true">
      <mat-icon>check</mat-icon>
    </span>
  } @else {
    <span
      class="translation-language-list__status-dot translation-language-list__status-dot--none"
      aria-hidden="true"></span>
  }
  @if (failureFor(lang.code); as failure) {
    <app-failed-translation-note
      class="translation-language-list__note"
      [failure]="failure"
      [value]="values()[lang.code] ?? ''"
      [fieldName]="displayName(lang)"
      [disabled]="isReadonly(lang.code) || isSuggesting()"
      [textId]="noteIdFor(lang.code)"
      [testId]="testIdFor(lang.code)"
      (useRejectedText)="setValue(lang.code, $event)" />
  }
</ng-template>
